import { parseStorefrontConfig, type StorefrontConfig } from './schema'

/** `{ [namespace]: { [key]: string } }` — the shape `texts` takes. */
export type TextLibrary = Record<string, Record<string, string>>

/**
 * What commerce-scs actually serves today: cinnamon's config format.
 *
 * Verified against
 * `storefront-configuration-manager-stg.yalochat.com/v1/storefronts/{slug}`,
 * whose `data.data` carries `analytics`, `botSlug`, `containers`, `defaultRoute`,
 * `globalErrorMessage`, `globals`, `logo`, `metatags`, `modals`, `name`,
 * `options`, `phoneNumber`, `routes`, `storeName`, `templates`, `theme` — and no
 * `texts` at all.
 *
 * Only the fields this storefront reads are modelled. `containers`, `templates`,
 * `modals` and `routes` are the interpreter's, and are deliberately ignored.
 */
export interface CinnamonConfig {
  name?: unknown
  storeName?: unknown
  logo?: unknown
  options?: Record<string, unknown>
  texts?: unknown
  metatags?: Record<string, unknown>
  theme?: { palette?: unknown }
  containers?: unknown
  templates?: unknown
}

/**
 * `null` from this backend means "not set" — `options.placeholderImage` is
 * literally `null` on a live storefront. Everything else is passed through
 * untouched so the schema, not this adapter, decides whether it is valid.
 */
const absentIfNull = (value: unknown): unknown => (value ?? undefined)

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/** `logoUrl` must be a URL or the empty string; anything else means "no logo". */
const asLogoUrl = (value: unknown): string =>
  typeof value === 'string' && /^https?:\/\//.test(value) ? value : ''

/**
 * cinnamon's `options.hidePrices` is an object (`{ enabled, customField,
 * flagValue }`), and it is the only place the per-customer rule is authored.
 * Some older storefronts carry a bare boolean there instead, which is not the
 * rule and must not reach the schema — a `z.object()` would reject it and take
 * the whole config down with it.
 */
const asHidePricesRule = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined
  const rule = value as Record<string, unknown>
  return {
    enabled: absentIfNull(rule.enabled),
    customField: absentIfNull(rule.customField),
    flagValue: absentIfNull(rule.flagValue),
  }
}

const isTextLibrary = (value: unknown): value is TextLibrary =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (namespace) =>
      typeof namespace === 'object' &&
      namespace !== null &&
      !Array.isArray(namespace) &&
      Object.values(namespace).every((entry) => typeof entry === 'string'),
  )

/**
 * Namespace-wise merge: a storefront overrides individual keys, not whole
 * namespaces.
 *
 * Exported because the same rule applies to a config already in the new schema.
 * Neither format is expected to restate the port's ~300 default strings, and
 * `config.texts.promotions.someKey` reading undefined renders a blank promotion
 * message rather than failing — so the merge has to happen on both paths, from
 * one implementation.
 */
export const mergeTextLibraries = (base: TextLibrary, over: TextLibrary): TextLibrary => {
  const merged: TextLibrary = { ...base }
  for (const [namespace, entries] of Object.entries(over)) {
    merged[namespace] = { ...(base[namespace] ?? {}), ...entries }
  }
  return merged
}

/**
 * Is this a cinnamon-format config rather than the new schema?
 *
 * `containers` and `templates` are the interpreter's own artifacts and appear in
 * nothing else, so either is conclusive. `options` alongside a missing `logoUrl`
 * catches a config that predates them.
 */
export const isCinnamonConfig = (raw: unknown): boolean => {
  const config = asRecord(raw)
  if ('containers' in config || 'templates' in config) return true
  return 'options' in config && !('logoUrl' in config)
}

/**
 * Map a cinnamon config onto the new schema, then validate it.
 *
 * The `options` block is almost exactly the field set `core`'s promotion engine
 * already reads, which is why this is a rename rather than a translation.
 *
 * `theme.palette` is deliberately **not** mapped into `branding`: the host
 * already feeds the stored palette through `fromMuiPalette`, and `branding` is
 * for what a storefront authors on top of it. Mapping it here would apply the
 * same colours twice and would fail the schema's hex check for any palette that
 * uses `rgb()`.
 *
 * @param defaultTexts cinnamon merges its own copy library at runtime and the
 *   served config contains none, so the promotion engine would otherwise find
 *   `texts.promotions` empty. Pass the port's own defaults for the storefront's
 *   locale; anything in the config overrides them key by key.
 */
export const fromCinnamonConfig = (
  raw: unknown,
  { defaultTexts = {} }: { defaultTexts?: TextLibrary } = {},
): StorefrontConfig => {
  const config = asRecord(raw) as CinnamonConfig
  const options = asRecord(config.options)
  const metatags = asRecord(config.metatags)

  return parseStorefrontConfig({
    name: absentIfNull(config.name),
    storeName: absentIfNull(config.storeName),
    logoUrl: asLogoUrl(config.logo),
    branding: {},
    commerce: {
      currencySymbol: absentIfNull(options.currencySymbol ?? options.currency),
      currencyCode: absentIfNull(options.currencyCode),
      priceFormat: absentIfNull(options.format),
      priceDisplay: absentIfNull(options.currencyDisplay),
      promotionPercentageFormat: absentIfNull(options.promotionPercentageFormat),
      locale: absentIfNull(options.localization),
      orderMinAmount: absentIfNull(options.orderMinAmount),
      hidePricesRule: asHidePricesRule(options.hidePrices),
      placeholderImage: absentIfNull(options.placeholderImage),
    },
    texts: mergeTextLibraries(
      defaultTexts,
      isTextLibrary(config.texts) ? config.texts : {},
    ),
    metatags: {
      title: absentIfNull(metatags.title),
      description: absentIfNull(metatags.description),
      favicon: absentIfNull(metatags.favicon),
    },
  })
}
